import React, { useEffect, useState } from "react";
import Logo from "../src/Assets/images/Logo-1.png";
import { Link, useLocation } from "react-router-dom";
import { HiMenu, HiX } from "react-icons/hi";
import Notification from "./components/common/Notification";
import ProfileDropdown from "./components/common/ProfileDropdown";
import { getFromStorage } from "./lib/storage";
import LoginPage from "./pages/LoginPage";
import { FiShoppingCart } from "react-icons/fi";
import { fetchCartItems } from "./api/cartService";
import { clearNotifications, getNotifications } from "./api/loginUser";
import toast from "react-hot-toast";

const Header = () => {
  const [cartCount, setCartCount] = useState(0);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false); // 🔹 new
  const [prevPath, setPrevPath] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [step, setStep] = useState("login")

  const prevPathRef = React.useRef(null);
  const location = useLocation();

  const token = getFromStorage("lmsCCToken");

  useEffect(() => {
    if (token) {
      fetchNotifications();
    }
  }, [location.pathname, token]);

  useEffect(() => {
    const refresh = () => fetchNotifications();
    window.addEventListener("notificationUpdated", refresh);
    return () => window.removeEventListener("notificationUpdated", refresh);
  }, []);

  useEffect(() => {
    if (!token) {
      localStorage.clear();
    }
  }, [token]);


  useEffect(() => {
    setPrevPath(location.pathname);
  }, [location]);


  // cart count logic
  useEffect(() => {
    const loadCartCount = async () => {
      if (token) {
        const res = await fetchCartItems();
        const response = res?.data?.responsePacket || {};
        const items = response.cartItems || [];
        setCartCount(items.length || 0);
      } else {
        const localItems =
          JSON.parse(localStorage.getItem("lmsCartItems")) || [];
        setCartCount(localItems.length);
      }
    };

    loadCartCount();
    const handleCartUpdate = () => loadCartCount();
    window.addEventListener("cartUpdated", handleCartUpdate);

    return () => {
      window.removeEventListener("cartUpdated", handleCartUpdate);
    };
  }, [token]);

  useEffect(() => {
    return () => {
      prevPathRef.current = location.pathname;
    };
  }, [location]);


  const isActive = (path) => {
    if (path === "/") {
      return (
        location.pathname === "/" ||
        (location.pathname.startsWith("/CourseDetails") &&
          prevPathRef.current === "/")
      );
    }

    if (path === "/Dashboard") {
      return (
        location.pathname.startsWith("/Dashboard") ||
        location.pathname.startsWith("/CourseLearningPage") ||
        (location.pathname.startsWith("/CourseDetails") &&
          prevPathRef.current?.startsWith("/Dashboard"))
      );
    }

    if (path === "/Cart") {
      return location.pathname.startsWith("/Cart");
    }

    if (path === "/about") {
      return location.pathname.toLowerCase().startsWith("/about");
    }

    return false;
  };

  const handleNotificationClick = async (uuid) => {
    const res = await clearNotifications(uuid, false);
    if (res?.errorCode === 0) toast.success("Marked as read");
    return res;
  };

  const handleClearAll = async () => {
    const res = await clearNotifications(null, true);
    if (res?.errorCode === 0) toast.success("All notifications marked as read");
    return res;
  };

  const fetchNotifications = async () => {
    try {
      const res = await getNotifications();
      if (res?.errorCode === 0) {
        setNotifications(res?.responsePacket || []);
      } else {
        setNotifications([]);
      }
    } catch (err) {
      console.error("Failed to fetch notifications", err);
    }
  };

  const toggleMenu = () => {
    setMenuOpen((prev) => {
      if (!prev) setProfileOpen(false)
      return !prev;
    });
  };

  const toggleProfile = () => {
    setProfileOpen((prev) => {
      if (!prev) setMenuOpen(false)
      return !prev;
    });
  };
  return (
    <header className="bg-white shadow-md fixed top-0 left-0 w-full z-50">
      <div className="mx-auto px-2 sm:px-4 md:px-6 lg:px-8">
        <div className="flex justify-between items-center py-2 sm:py-3">
          <div className="flex items-center gap-2">
            <Link to="/" className="flex-shrink-0">
              <img
                // src={Logo}
                // alt="Logo"
                className="w-20 sm:w-24 md:w-32 lg:w-40 object-contain"
              />
            </Link>
            <span className="bg-yellow-400 text-gray-900 text-[10px] sm:text-xs font-bold px-1.5 sm:px-2 py-0.5 rounded">
              BETA
            </span>
          </div>

          <nav className="hidden md:flex space-x-2 lg:space-x-4 font-medium text-sm lg:text-base items-center">
            <Link
              to="/"
              className={`px-2 lg:px-3 py-1.5 lg:py-2 rounded-md transition-colors duration-200 ${isActive("/") ? "text-gray-900 font-semibold" : "text-gray-700 hover:text-gray-900"
                }`}
            >
              Home
            </Link>
            <Link
              to="/about"
              className={`px-2 lg:px-3 py-1.5 lg:py-2 rounded-md transition-colors duration-200 ${isActive("/about") ? "text-gray-900 font-semibold" : "text-gray-700 hover:text-gray-900"
                }`}
            >
              About Us
            </Link>
            {token && (
              <Link
                to="/Dashboard"
                className={`px-2 lg:px-3 py-1.5 lg:py-2 rounded-md transition-colors duration-200 ${isActive("/Dashboard")
                  ? "text-gray-900 font-semibold"
                  : "text-gray-700 hover:text-gray-900"
                  }`}
              >
                Dashboard
              </Link>
            )}
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
            {token && (
              <Notification
                setNotifications={setNotifications}
                notifications={notifications}
                onNotificationClick={handleNotificationClick}
                onClearAll={handleClearAll}
                getNotifications={getNotifications}
              />
            )}

            <Link to="/cartPage" className="relative">
              <FiShoppingCart className="w-5 h-5 sm:w-6 sm:h-6 text-black" />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-600 text-white text-[10px] sm:text-xs w-4 h-4 sm:w-5 sm:h-5 rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Link>

            {!token ? (
              <button
                onClick={() => {
                  setIsLoginModalOpen(true)
                  sessionStorage.setItem('isenroll', 'false')
                }}
                className="px-3 sm:px-4 py-1.5 sm:py-2 bg-gray-900 text-white rounded-lg text-xs sm:text-sm hover:bg-gray-800"
              >
                Login / Sign Up
              </button>
            ) : (
              <ProfileDropdown
                setStep={setStep}
                isOpen={profileOpen}
                setMenuOpen={setMenuOpen}
                toggleProfile={toggleProfile}
              />
            )}

            <button
              className="md:hidden text-2xl text-gray-700"
              onClick={toggleMenu}
            >
              {menuOpen ? <HiX /> : <HiMenu />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden bg-white shadow-md px-4 sm:px-6 py-3 space-y-2 text-sm sm:text-base">
          <Link
            to="/"
            className={`block px-2 py-1.5 rounded-md transition-colors duration-200 ${isActive("/") ? "text-gray-900 font-semibold" : "text-gray-700 hover:text-gray-900"
              }`}
            onClick={() => setMenuOpen(false)}
          >
            Home
          </Link>
          <Link
            to="/about"
            className={`block px-2 py-1.5 rounded-md transition-colors duration-200 ${isActive("/about") ? "text-gray-900 font-semibold" : "text-gray-700 hover:text-gray-900"
              }`}
            onClick={() => setMenuOpen(false)}
          >
            About Us
          </Link>
          {token && (
            <Link
              to="/Dashboard"
              className={`block px-2 py-1.5 rounded-md transition-colors duration-200 ${isActive("/Dashboard")
                ? "text-gray-900 font-semibold"
                : "text-gray-700 hover:text-gray-900"
                }`}
              onClick={() => setMenuOpen(false)}
            >
              Dashboard
            </Link>
          )}
        </div>
      )}

      {/* Login modal */}
      {isLoginModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50">
          <div className={`bg-white rounded-xl shadow-lg w-full mx-2 sm:mx-4 relative max-h-[90vh] overflow-y-auto ${
            step === 'personal' ? 'max-w-3xl' : 'max-w-sm sm:max-w-md'
          }`}>
            {step !== 'personal' ? <button
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
              onClick={() => {
                setIsLoginModalOpen(false)
                setStep('login')
              }}
            >
              ✕
            </button> : null}
            <LoginPage
              step={step}
              setStep={setStep}
              isLoginModalOpen={isLoginModalOpen}
              setIsLoginModalOpen={setIsLoginModalOpen}
            />
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
